import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Badge, Spinner, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiSearch,
  FiRefreshCw,
  FiExternalLink,
  FiUser,
  FiMail,
  FiPhone,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { BiBrain } from "react-icons/bi";
import WebService from "../../Services/WebService";
import HelperService from "../../Services/HelperService";
import defaultAvatar from "../../assets/Images/default-avatar.jpg";
import moment from "moment";
import { toast } from "react-toastify";
import "./QuickActionWidget.scss";

export interface ProfileAiErrorItem {
  id: number;
  user_id: number;
  analysis_type: string;
  last_error: string;
  has_to_update: number;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
  user_uuid: string;
  business_uuid?: string | null;
  first_name: string;
  last_name: string;
  user_name: string;
  email: string;
  dial_code: string;
  mobile_number: string;
  profile_image: string | null;
  status: string;
  user_type: string;
}

interface ProfileAiErrorsModalProps {
  show: boolean;
  onHide: () => void;
  onRefreshCount?: () => void;
}

const TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Capital Bridge", value: "capital-bridge" },
  { label: "Investor", value: "investor" },
  { label: "Global Connect", value: "want-to-grow" },
  { label: "Business Exchange", value: "want-to-sell" },
];

const ProfileAiErrorsModal: React.FC<ProfileAiErrorsModalProps> = ({
  show,
  onHide,
  onRefreshCount,
}) => {
  const navigate = useNavigate();
  const [list, setList] = useState<ProfileAiErrorItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [nextOffset, setNextOffset] = useState<number>(-1);
  const [keyword, setKeyword] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const fetchErrors = useCallback(
    (offset = 0, isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams();
      if (keyword.trim()) params.append("keyword", keyword.trim());
      if (selectedType) params.append("type", selectedType);
      params.append("offset", String(offset));
      params.append("per_page", "10");

      WebService.getAPI({
        action: `quick-actions/profile-ai-errors?${params.toString()}`,
        body: null,
        isShowError: false,
      })
        .then((res: any) => {
          const rawList = res?.list || res?.data?.list;
          if (res?.status && Array.isArray(rawList)) {
            const dataList: ProfileAiErrorItem[] = rawList;
            if (isLoadMore) {
              setList((prev) => [...prev, ...dataList]);
            } else {
              setList(dataList);
            }
            setTotalCount(res.count ?? res.data?.count ?? dataList.length);
            setNextOffset(res.next_offset ?? res.data?.next_offset ?? -1);
            if (onRefreshCount) onRefreshCount();
          } else {
            if (!isLoadMore) setList([]);
            setNextOffset(-1);
          }
        })
        .catch(() => {
          if (!isLoadMore) setList([]);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [keyword, selectedType, onRefreshCount]
  );

  useEffect(() => {
    if (show) {
      fetchErrors(0, false);
    }
  }, [show, fetchErrors]);

  const handleNavigateToAnalysis = (item: ProfileAiErrorItem) => {
    const analysisType = item.analysis_type || "capital-bridge";
    const userUuid = item.user_uuid;
    if (userUuid) {
      onHide();
      navigate(`/profile-analysis/${analysisType}/${userUuid}`);
    } else {
      toast.error("User identifier is missing for this analysis.");
    }
  };

  const getEntrepreneurProfileType = (analysisType?: string) => {
    const t = (analysisType || "").toLowerCase().replace(/_/g, "-");
    if (t.includes("grow") || t.includes("global")) return "want_to_grow";
    if (t.includes("sell") || t.includes("exchange")) return "want_to_sell";
    return "investment";
  };

  const handleNavigateToUser = (item: ProfileAiErrorItem) => {
    onHide();
    const aType = (item.analysis_type || "").toLowerCase();
    if (item.user_type === "INVESTOR" || aType === "investor") {
      navigate(`/manage-investor/${item.user_uuid}`);
    } else {
      const entrepreneurType = getEntrepreneurProfileType(item.analysis_type);
      const tabName =
        entrepreneurType === "want_to_grow"
          ? "Want_To_Grow"
          : entrepreneurType === "want_to_sell"
            ? "Want_To_Sell"
            : "Investment";
      localStorage.setItem("SelectedDetailedTab", tabName);
      const targetId = item.business_uuid || item.user_uuid;
      navigate(`/manage-entrepreneur/${targetId}/${entrepreneurType}`);
    }
  };

  const handleReanalyzeDirect = (item: ProfileAiErrorItem) => {
    if (!item.user_uuid || retryingId !== null) return;
    setRetryingId(item.id);
    WebService.postAPI({
      action: `profile-analysis/${item.analysis_type || "capital-bridge"}/${item.user_uuid}/reanalyze`,
      body: {},
      isShowError: true,
    })
      .then((res: any) => {
        if (res) {
          toast.success("Reanalysis triggered successfully.");
          fetchErrors(0, false);
        }
      })
      .catch(() => false)
      .finally(() => setRetryingId(null));
  };

  const formatMobile = (dialCode?: string, mobile?: string) => {
    if (!mobile) return "-";
    if (!dialCode) return mobile;
    const cleanDial = dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
    return `${cleanDial} ${mobile}`;
  };

  const parseLastError = (
    rawError: any
  ): Array<{ page?: string; message: string }> => {
    if (!rawError) return [];

    // If it's already an array
    if (Array.isArray(rawError)) {
      return rawError
        .map((item) => {
          if (typeof item === "string") {
            const tr = item.trim();
            if (
              (tr.startsWith("{") && tr.endsWith("}")) ||
              (tr.startsWith("[") && tr.endsWith("]"))
            ) {
              try {
                return parseLastError(JSON.parse(tr));
              } catch (e) { }
            }
            return [{ message: tr }];
          }
          return [
            {
              page: item?.page || item?.section || item?.page_name,
              message:
                item?.message ||
                item?.error ||
                item?.description ||
                JSON.stringify(item),
            },
          ];
        })
        .flat();
    }

    // If it's an object (not array, not string)
    if (typeof rawError === "object" && rawError !== null) {
      return [
        {
          page: rawError?.page || rawError?.section || rawError?.page_name,
          message:
            rawError?.message ||
            rawError?.error ||
            rawError?.description ||
            JSON.stringify(rawError),
        },
      ];
    }

    // If it's a string, try JSON parsing
    if (typeof rawError === "string") {
      const trimmed = rawError.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          return parseLastError(parsed);
        } catch (e) {
          // Fallback to raw string if JSON.parse fails
        }
      }
      return [{ message: trimmed }];
    }

    return [{ message: String(rawError) }];
  };

  const resolveProfileImage = (imagePath?: string | null): string => {
    if (!imagePath || typeof imagePath !== "string") return defaultAvatar;
    const trimmed = imagePath.trim();
    if (
      !trimmed ||
      trimmed === "null" ||
      trimmed === "undefined" ||
      trimmed === "-" ||
      trimmed.endsWith("default-avatar.jpg")
    ) {
      return defaultAvatar;
    }
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:")
    ) {
      return trimmed;
    }

    const baseUrl = (process.env.REACT_APP_BASE_URL || "")
      .replace(/\/admin\/?$/i, "")
      .replace(/\/api\/?$/i, "")
      .replace(/\/+$/, "");
    const cleanPath = trimmed.replace(/^\/+/, "");
    return `${baseUrl}/${cleanPath}`;
  };

  const getInitials = (name?: string) => {
    if (!name || !name.trim()) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name?: string) => {
    const gradients = [
      "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
      "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
    ];
    if (!name) return gradients[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  };

  const getAnalysisTypeBadgeColor = (type: string) => {
    const t = (type || "").toLowerCase();
    if (t.includes("investor")) return { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
    if (t.includes("grow") || t.includes("global")) return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
    if (t.includes("sell") || t.includes("exchange")) return { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" };
    return { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" };
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="qa-modal"
      backdrop="static"
    >
      <Modal.Header closeButton className="qa-modal-header border-0 pb-0">
        <div className="d-flex align-items-start gap-3 flex-grow-1">
          <div className="qa-header-icon-box">
            <FiAlertTriangle size={24} className="text-warning" />
          </div>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="modal-title fw-bold text-dark mb-0">
                Profile AI Analysis Issues
              </h5>
              <Badge bg="danger" pill className="px-2 py-1 font-12">
                High Priority
              </Badge>
              <Badge bg="warning" text="dark" pill className="px-2 py-1 font-12">
                {totalCount} {totalCount === 1 ? "Issue" : "Issues"}
              </Badge>
            </div>
            <p className="text-muted font-13 mb-0 mt-1">
              Profiles where AI analysis encountered processing errors, timeouts, or latency.
              Review details and inspect or trigger re-analysis.
            </p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="qa-modal-body pt-3">
        {/* Filter & Search Bar */}
        <div className="qa-filter-bar mb-3 p-3 rounded-3 bg-light border">
          <div className="row g-2 align-items-center">
            <div className="col-lg-6 col-md-12">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch size={16} className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, phone, UUID, or error..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="border-start-0 ps-0 shadow-none font-13"
                />
                {keyword && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setKeyword("")}
                    className="border-start-0"
                  >
                    ✕
                  </Button>
                )}
              </div>
            </div>

            <div className="col-lg-4 col-md-8 col-8">
              <Form.Select
                size="sm"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="shadow-none font-13 h-100"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-lg-2 col-md-4 col-4 text-end">
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100 d-flex align-items-center justify-content-center gap-1 font-13"
                onClick={() => fetchErrors(0, false)}
                disabled={loading}
              >
                <FiRefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted font-13 mt-2 mb-0">
              Loading AI profile analysis issues...
            </p>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-5 my-3 bg-light rounded-3 border">
            <div className="mb-2">
              <FiCheckCircle size={44} className="text-success" />
            </div>
            <h6 className="fw-bold text-dark">No AI Analysis Errors Found</h6>
            <p className="text-muted font-13 mb-0">
              {keyword || selectedType
                ? "No issues match your search criteria."
                : "All profile analyses are currently healthy and up to date."}
            </p>
          </div>
        ) : (
          <div className="qa-error-list">
            {list.map((item) => {
              const typeStyle = getAnalysisTypeBadgeColor(item.analysis_type);
              const fullName =
                item.user_name ||
                `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
                "Unknown User";

              return (
                <div
                  key={`err_${item.id}_${item.user_uuid}`}
                  className="qa-error-card p-3 mb-3 rounded-3 border bg-white shadow-sm"
                >
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                    {/* User info left side */}
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="qa-avatar-wrapper flex-shrink-0"
                        style={{ width: "44px", height: "44px", minWidth: "44px", minHeight: "44px" }}
                        onClick={() => handleNavigateToUser(item)}
                        title={`View ${fullName}'s profile`}
                      >
                        <img
                          src={resolveProfileImage(item.profile_image)}
                          alt={fullName}
                          className="qa-user-avatar rounded-circle"
                          style={{
                            width: "44px",
                            height: "44px",
                            minWidth: "44px",
                            minHeight: "44px",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.onerror = null;
                            target.src = defaultAvatar;
                          }}
                        />
                        {item.status && (
                          <span
                            className={`qa-status-dot ${item.status === "ACTIVE"
                              ? "status-active"
                              : "status-inactive"
                              }`}
                            title={`Status: ${item.status}`}
                          />
                        )}
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span
                            className="fw-bold text-dark font-15 qa-user-link"
                            onClick={() => handleNavigateToUser(item)}
                            title="View User Details"
                          >
                            {fullName}
                          </span>
                          <span
                            className="badge font-11 px-2 py-1 rounded-pill"
                            style={{
                              backgroundColor: typeStyle.bg,
                              color: typeStyle.color,
                              border: `1px solid ${typeStyle.border}`,
                            }}
                          >
                            {HelperService.removeUnderScore(item.analysis_type || "capital-bridge")}
                          </span>
                          {item.status && (
                            <span
                              className={`badge font-10 ${item.status === "ACTIVE"
                                ? "bg-success"
                                : "bg-secondary"
                                }`}
                            >
                              {item.status}
                            </span>
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-3 font-12 text-muted mt-1 flex-wrap">
                          {item.email && (
                            <span className="d-flex align-items-center gap-1">
                              <FiMail size={12} />
                              {item.email}
                            </span>
                          )}
                          {item.mobile_number && (
                            <span className="d-flex align-items-center gap-1">
                              <FiPhone size={12} />
                              {formatMobile(item.dial_code, item.mobile_number)}
                            </span>
                          )}
                          <span className="d-flex align-items-center gap-1">
                            <FiClock size={12} />
                            {item.updated_at
                              ? moment(item.updated_at).format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons right side */}
                    <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="d-flex align-items-center gap-1 font-12 px-3"
                        onClick={() => handleNavigateToUser(item)}
                      >
                        <FiUser size={13} />
                        Profile
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="d-flex align-items-center gap-1 font-12 px-3 text-dark"
                        onClick={() => handleReanalyzeDirect(item)}
                        disabled={retryingId === item.id}
                      >
                        <FiRefreshCw
                          size={13}
                          className={retryingId === item.id ? "spin" : ""}
                        />
                        {retryingId === item.id ? "Reanalyzing..." : "Retry AI"}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="d-flex align-items-center gap-1 font-12 px-3"
                        onClick={() => handleNavigateToAnalysis(item)}
                      >
                        <BiBrain size={14} />
                        View Analysis
                        <FiExternalLink size={12} />
                      </Button>
                    </div>
                  </div>

                  {/* Error Callout */}
                  {(() => {
                    const parsedErrors = parseLastError(item.last_error);
                    if (parsedErrors.length === 0) return null;

                    return (
                      <div className="qa-error-container mt-2">
                        {parsedErrors.map((err, errIdx) => (
                          <div
                            key={`err_detail_${errIdx}`}
                            className="qa-error-box p-2 px-3 rounded-2 mb-1"
                            style={{
                              background: "#fffbeb",
                              border: "1px solid #fde68a",
                              color: "#92400e",
                            }}
                          >
                            <div className="d-flex align-items-start gap-2">
                              <FiAlertTriangle
                                size={16}
                                className="text-warning mt-1 flex-shrink-0"
                              />
                              <div
                                className="flex-grow-1 font-12"
                                style={{ lineHeight: "1.5" }}
                              >
                                {err.page ? (
                                  <span
                                    className="badge me-2"
                                    style={{
                                      backgroundColor: "#fef3c7",
                                      color: "#92400e",
                                      border: "1px solid #fcd34d",
                                      fontWeight: 600,
                                      fontSize: "11px",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.4px",
                                    }}
                                  >
                                    {HelperService.removeUnderScore(err.page)}
                                  </span>
                                ) : (
                                  <strong className="text-dark me-1">
                                    Last Error:
                                  </strong>
                                )}
                                <span style={{ color: "#78350f" }}>
                                  {err.message}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })}

            {/* Load More Button */}
            {nextOffset !== -1 && (
              <div className="text-center mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fetchErrors(nextOffset, true)}
                  disabled={loadingMore}
                  className="px-4 font-13"
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading more...
                    </>
                  ) : (
                    "Load More Issues"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="qa-modal-footer border-0 pt-0">
        <div className="d-flex justify-content-between align-items-center w-100">
          <span className="font-12 text-muted">
            Showing {list.length} of {totalCount} total issues
          </span>
          <Button variant="secondary" size="sm" onClick={onHide} className="px-4">
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileAiErrorsModal;
